import { cn } from "@/shared/lib/css";

interface CountryFlagProps {
  countryCode: string;
  className?: string;
}

/**
 * Country flag component using emoji flags (0 KB bundle size)
 * Falls back to globe emoji for unknown codes
 */
const SPECIAL_CODES: Record<string, string> = {
  MIX: "🔀",
};

export function CountryFlag({ countryCode, className }: CountryFlagProps) {
  if (!countryCode) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center leading-none",
          className,
        )}
      >
        🌍
      </span>
    );
  }

  const normalizedCode = countryCode.toUpperCase();

  if (normalizedCode in SPECIAL_CODES) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center leading-none",
          className,
        )}
      >
        {SPECIAL_CODES[normalizedCode]}
      </span>
    );
  }

  const flagEmoji = getFlagEmoji(normalizedCode);

  if (flagEmoji) {
    return (
      <span
        className={cn(
          "inline-flex items-center justify-center leading-none",
          className,
        )}
      >
        {flagEmoji}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center leading-none",
        className,
      )}
    >
      🌍
    </span>
  );
}

/**
 * Converts country code to emoji flag
 * @param countryCode - ISO 3166-1 alpha-2 country code (e.g., 'US', 'UA')
 * @returns Emoji flag or null if invalid code
 */
function getFlagEmoji(countryCode: string): string | null {
  if (countryCode.length !== 2) {
    return null;
  }

  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 127397 + char.charCodeAt(0));

  return String.fromCodePoint(...codePoints);
}
